// How a cross-ledger hand match must cancel. MIRRORS backend.manual_overrides
// hand_match_rules / hand_match_residual: the engine sends the rules
// (result.manual_match_rules) and both sides apply them identically, so the match bar can
// never promise "Matched" for a pair the engine puts on Group 3.
//
// The Matched line adds CRI to insurer under the declared sign frame, so legs that only
// agree on magnitude (both negative, say) do not settle: AL BUHAIRA Aug-26 a -122,314.75
// reversal journal against a -122,314.75 receipt left the line at -244,629.51.

import type { FrameRecords } from "../types/api";
import { sigPosMap } from "./columns";

export interface HandMatchSignRules {
  sign_frame: "inverted" | "same" | null;
  abs_tracks: string[];
  conventions: Record<string, "opposite" | "same">;
  // The buckets a leg may sit in (backend HAND_MATCH_CATEGORIES / HAND_MATCH_TRACKS, null
  // = every track), and those refused when the match routes to Cleared in Subsequent Month
  // (HAND_MATCH_TIE_ONLY_CATEGORIES).
  open_categories?: { cri: string[]; insurer: string[] };
  tracks?: string[] | null;
  tie_only_categories?: { cri: string[]; insurer: string[] };
}

// Why each ticked row cannot be hand-matched, or [] (backend _off_bucket_legs): in the
// window and in a bucket a hand match does not settle (a balance row), or a journal routed
// to the Difference in a match settled after the period. `inPeriod`: the match is decided
// this period (Matched or Group 3), not routed to Cleared. An out-of-window row is judged
// on its track only — the engine lists it opposite its partner. In-window is the engine's
// in_period_window, not period_position: an undated row is "In period" there yet outside
// the window.
export function offBucketReasons(frame: FrameRecords, sigs: string[], side: "cri" | "insurer",
                                 rules: HandMatchSignRules | null | undefined,
                                 inPeriod = false): string[] {
  if (!rules?.open_categories || !sigs.length) return [];
  const col = (n: string) => frame.columns.indexOf(n);
  const ti = col("track"), wi = col("in_period_window"), ci = col("recon_category");
  if (col("_sig") < 0) return [];
  const tieOnly = rules.tie_only_categories?.[side] ?? [];
  const pos = sigPosMap(frame);
  const out: string[] = [];
  for (const s of sigs) {
    const p = pos.get(s);
    if (p === undefined) continue;
    const r = frame.rows[p];
    const t = ti >= 0 ? String(r[ti] ?? "").trim().toLowerCase() : "";
    if (ti >= 0 && rules.tracks && !rules.tracks.includes(t) && t !== "nan" && t !== "none") {
      out.push(`a row on the ${t} track (no Group tab lists that track)`);
      continue;
    }
    const inWindow = wi < 0 || (r[wi] !== false && r[wi] !== 0);
    const cat = ci >= 0 ? String(r[ci] ?? "") : "";
    if (!inWindow || ci < 0) continue;
    if (tieOnly.includes(cat) && !inPeriod) {
      out.push(`a row in '${cat}' (the Difference) settled after the period — it was still `
               + "unexplained at period end");
    } else if (!rules.open_categories[side].includes(cat)) {
      out.push(`a row in '${cat}' (a balance, not a transaction a hand match settles)`);
    }
  }
  return [...new Set(out)];
}

// Whether any ticked row is dated after the period (the engine then routes a tying match
// to Cleared in Subsequent Month, backend manual_cleared._after_period).
export function hasAfterPeriodLeg(frame: FrameRecords, sigs: string[]): boolean {
  const pi = frame.columns.indexOf("period_position");
  if (pi < 0 || frame.columns.indexOf("_sig") < 0) return false;
  const pos = sigPosMap(frame);
  return sigs.some((s) => {
    const p = pos.get(s);
    return p !== undefined && frame.rows[p][pi] === "After period";
  });
}

// Whether any ticked row is one of `unlisted` (frame indices, result.unlisted_pre_period_rows).
// The engine refuses such a row in a match that ties across both ledgers and is not routed
// to Cleared (backend manual_overrides._settles_unlisted); the caller judges that shape.
export function hasUnlistedLeg(frame: FrameRecords, sigs: string[],
                               unlisted: (number | string)[] | undefined): boolean {
  if (!unlisted?.length || frame.columns.indexOf("_sig") < 0) return false;
  const pre = new Set(unlisted.map(String));
  const pos = sigPosMap(frame);
  return sigs.some((s) => {
    const p = pos.get(s);
    return p !== undefined && pre.has(String(frame.indices[p]));
  });
}

// The match bar's words for that refusal (backend carried_rolled.NOT_OPEN_AT_START).
export const UNLISTED_LEG_NOTE =
  "a row dated before the period that no prior-period list you uploaded carries, in a "
  + "match that ties across both ledgers (if it was open at the period start, it is missing "
  + "from that list)";

// Why a pair that agrees on magnitude still does not settle, in finance's words.
export const SIGN_MISMATCH_NOTE =
  "same amount, but the signs do not cancel the way this insurer's matched rows do — "
  + "stays in Figure/Balance Mismatch";

// +1 when an insurer leg on `track` cancels CRI by addition, -1 by subtraction, null when
// only magnitudes can be compared (no declared frame, or an undecided magnitude track).
export function insurerLegSign(rules: HandMatchSignRules | null | undefined,
                               track: unknown): number | null {
  if (!rules || !rules.sign_frame) return null;
  const t = String(track ?? "").trim().toLowerCase();
  if ((rules.abs_tracks ?? []).includes(t)) {
    const conv = (rules.conventions ?? {})[t];
    return conv === undefined ? null : conv === "opposite" ? 1 : -1;
  }
  return rules.sign_frame === "inverted" ? 1 : -1;
}

// What the match leaves unsettled: ΣCRI + Σ(sign × insurer), or the magnitude gap
// | |ΣCRI| − |Σinsurer| | when any insurer leg's track has no stated convention.
export function handMatchResidual(criAmounts: number[], insAmounts: number[],
                                  insTracks: unknown[],
                                  rules: HandMatchSignRules | null | undefined): number {
  const signs = insTracks.map((t) => insurerLegSign(rules, t));
  const c = criAmounts.reduce((a, b) => a + b, 0);
  if (!signs.length || signs.some((s) => s === null)) {
    return Math.abs(Math.abs(c) - Math.abs(insAmounts.reduce((a, b) => a + b, 0)));
  }
  return Math.abs(c + signs.reduce<number>((t, s, i) => t + (s as number) * insAmounts[i], 0));
}
